import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TransformedProduct } from "../../types/productApiTypes";
import Icon from "../../../../../common/components/Icon/Icon";

interface ProductSingleTabsProps {
  product: TransformedProduct | null | undefined;
  isProductLoading: boolean;
  isProductError: boolean;
}

const ProductSingleTabs: React.FC<ProductSingleTabsProps> = ({
  product,
  isProductLoading,
  isProductError,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    "description" | "info" | "delivery" | null
  >(null);

  const { hasDescription, hasAdditionalInfo, hasLogistics, displayAttributes } =
    useMemo(() => {
      if (!product) {
        return {
          hasDescription: false,
          hasAdditionalInfo: false,
          hasLogistics: false,
          displayAttributes: [],
        };
      }
      const displayAttributes = product.enrichedAttributes;
      const hasDescription = !!product.description;
      const hasAdditionalInfo =
        product.weight ||
        (product.dimensions?.length &&
          product.dimensions?.width &&
          product.dimensions?.height) ||
        displayAttributes.length > 0;

      // Only show logistics tab if the product is NOT virtual AND data exists
      const hasLogistics =
        !product.virtual &&
        !!(product.deliveryEstimation || product.returnAvailability);

      return {
        hasDescription,
        hasAdditionalInfo,
        hasLogistics,
        displayAttributes,
      };
    }, [product]);

  useEffect(() => {
    const defaultTab = hasDescription
      ? "description"
      : hasAdditionalInfo
        ? "info"
        : hasLogistics
          ? "delivery"
          : null;
    setActiveTab(defaultTab);
  }, [hasDescription, hasAdditionalInfo, hasLogistics]);

  if (isProductLoading || isProductError || !areTranslationsReady || !product) {
    return null;
  }

  if (!hasDescription && !hasAdditionalInfo && !hasLogistics) {
    return null;
  }

  // --- Helper to format delivery days range ---
  const getDeliveryDaysString = () => {
    if (!product.deliveryEstimation) return "";
    const { minDays, maxDays } = product.deliveryEstimation;

    const daysLabel = t("delivery.businessDays", "Business Days");

    if (minDays === maxDays) {
      return `${minDays} ${daysLabel}`;
    }
    return `${minDays} - ${maxDays} ${daysLabel}`;
  };

  return (
    <div className="product-single-tabs">
      <div className="product-single-tabs__nav" role="tablist">
        {hasDescription && (
          <button
            className={`product-single-tabs__nav-item ${
              activeTab === "description" ? "is-active" : ""
            }`}
            role="tab"
            aria-selected={activeTab === "description"}
            aria-controls="tab-description"
            onClick={() => setActiveTab("description")}
          >
            {t("productSingle.tabs.description", "Description")}
          </button>
        )}
        {hasAdditionalInfo && (
          <button
            className={`product-single-tabs__nav-item ${
              activeTab === "info" ? "is-active" : ""
            }`}
            role="tab"
            aria-selected={activeTab === "info"}
            aria-controls="tab-info"
            onClick={() => setActiveTab("info")}
          >
            {t("productSingle.tabs.additionalInfo", "Additional Information")}
          </button>
        )}
        {hasLogistics && (
          <button
            className={`product-single-tabs__nav-item ${
              activeTab === "delivery" ? "is-active" : ""
            }`}
            role="tab"
            aria-selected={activeTab === "delivery"}
            aria-controls="tab-delivery"
            onClick={() => setActiveTab("delivery")}
          >
            {t("productSingle.tabs.deliveryReturns", "Delivery & Returns")}
          </button>
        )}
      </div>

      <div className="product-single-tabs__content">
        {hasDescription && (
          <div
            id="tab-description"
            className={`product-single-tabs__panel ${
              activeTab === "description" ? "is-active" : ""
            }`}
            role="tabpanel"
            hidden={activeTab !== "description"}
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        )}
        {hasAdditionalInfo && (
          <div
            id="tab-info"
            className={`product-single-tabs__panel ${
              activeTab === "info" ? "is-active" : ""
            }`}
            role="tabpanel"
            hidden={activeTab !== "info"}
          >
            <table className="product-single-tabs__info-table">
              <tbody>
                {product.weight && (
                  <tr>
                    <th>{t("productSingle.tabs.weight", "Weight")}</th>
                    <td>
                      {product.weight} {product.weightUnit}
                    </td>
                  </tr>
                )}
                {product.dimensions?.length &&
                  product.dimensions?.width &&
                  product.dimensions?.height && (
                    <tr>
                      <th>
                        {t("productSingle.tabs.dimensions", "Dimensions")}
                      </th>
                      <td>
                        {product.dimensions.length} &times;{" "}
                        {product.dimensions.width} &times;{" "}
                        {product.dimensions.height} {product.dimensionUnit}
                      </td>
                    </tr>
                  )}
                {displayAttributes.map((attribute) => (
                  <tr key={attribute.id}>
                    <th>{attribute.name}</th>
                    <td>
                      {attribute.terms.map((term) => term.name).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasLogistics && (
          <div
            id="tab-delivery"
            className={`product-single-tabs__panel ${
              activeTab === "delivery" ? "is-active" : ""
            }`}
            role="tabpanel"
            hidden={activeTab !== "delivery"}
          >
            <div className="logistics-info-grid">
              {product.deliveryEstimation && (
                <div className="logistics-info-item">
                  <div className="logistics-info-item__header">
                    <Icon iconName="truck" className="icon--stroke-thick" />
                    <h4>{t("delivery.estimateLabel", "Estimated Delivery")}</h4>
                  </div>
                  {/* Display Days Range instead of Dates */}
                  <p>{getDeliveryDaysString()}</p>
                  <small>
                    {t(
                      "delivery.disclaimer",
                      "Final dates calculated at checkout.",
                    )}
                  </small>
                </div>
              )}

              {product.returnAvailability && (
                <div className="logistics-info-item">
                  <div className="logistics-info-item__header">
                    <Icon
                      iconName={
                        product.returnAvailability.isReturnable
                          ? "arrow-right"
                          : "x-mark"
                      }
                      className="icon--stroke-thick"
                    />
                    <h4>{t("returnPolicy.label", "Return Policy")}</h4>
                  </div>
                  <p>{product.returnAvailability.displayString}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSingleTabs;
